import styled from 'styled-components';
import theme from '@raptormaps/theme';

// ── Page wrapper ─────────────────────────────────────────────────────

export const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;
`;

// ── Page header row ──────────────────────────────────────────────────

export const PageHeaderRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem 0 0.25rem;
`;

export const PageHeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

export const PageHeaderRight = styled.div`
  display: flex;
  gap: 0.625rem;
  flex-shrink: 0;
  align-items: center;
`;

export const Breadcrumb = styled.span`
  font-size: 0.75rem;
  color: ${theme.colors.slate_500};
  letter-spacing: 0.02em;
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

// ── ACTIVE badge ─────────────────────────────────────────────────────

export const ActiveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.65rem;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  background: ${theme.colors.green_100};
  color: ${theme.colors.green_800};
  flex-shrink: 0;
`;

// ── Feature permissions grid ──────────────────────────────────────────

export const PermissionCardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 0.875rem;
  padding-top: 0.5rem;
`;

export const PermissionFeatureCard = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid ${theme.colors.slate_200};
  border-radius: 8px;
  background: ${theme.colors.white};
`;

export const PermissionFeatureInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  min-width: 0;
`;

export const PermissionControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
  padding-bottom: 1rem;
  margin-top: 16px;
`;

export const ModernSearchWrapper = styled.div<{ $width?: string }>`
  position: relative;
  display: flex;
  align-items: center;
  width: ${({ $width }) => $width ?? '240px'};
  border: 1.5px solid ${theme.colors.slate_200};
  border-radius: 9999px;
  background: ${theme.colors.white};
  padding: 0 0.625rem;
  gap: 0.4rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: ${theme.colors.primary_400};
    box-shadow: 0 0 0 3px ${theme.colors.primary_100};
  }
`;

export const ModernSearchIcon = styled.span`
  display: flex;
  align-items: center;
  color: ${theme.colors.slate_400};
  flex-shrink: 0;
  pointer-events: none;
`;

export const ModernSearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.8125rem;
  color: ${theme.colors.grey_900};
  padding: 0.4rem 0;
  min-width: 0;

  &::placeholder {
    color: ${theme.colors.slate_400};
  }
`;

export const ModernSearchClear = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.125rem;
  color: ${theme.colors.slate_400};
  line-height: 1;
  flex-shrink: 0;
  border-radius: 50%;
  transition: color 0.1s ease, background 0.1s ease;

  &:hover {
    color: ${theme.colors.grey_900};
    background: ${theme.colors.slate_100};
  }
`;

export const FlagSearchWrapper = ModernSearchWrapper;
export const FlagSearchIcon = ModernSearchIcon;
export const FlagSearchInput = ModernSearchInput;
export const FlagSearchClear = ModernSearchClear;

export const SelectAllLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  font-weight: 500;
  color: ${theme.colors.slate_600};
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
`;

export const PermissionGroupSection = styled.div`
  &:not(:first-child) {
    margin-top: 1.25rem;
    padding-top: 1.25rem;
    border-top: 1px solid ${theme.colors.slate_100};
  }
`;

export const PermissionFeatureDescription = styled.span`
  font-size: 0.75rem;
  color: ${theme.colors.slate_500};
  line-height: 1.4;
`;

// ── Feature permissions card rows ────────────────────────────────────

export const FeatureRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.625rem 0;
  border-bottom: 1px solid ${theme.colors.slate_200};

  &:last-child {
    border-bottom: none;
  }
`;

// ── Identification card — 3-column layout ────────────────────────────

export const IdentificationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  padding-top: 0.25rem;
`;

export const IdentificationColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1rem 1.5rem 1rem 0;

  &:not(:last-child) {
    border-right: 1px solid ${theme.colors.slate_200};
    margin-right: 1.5rem;
  }
`;

export const IdentificationField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

export const FieldLabel = styled.span`
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${theme.colors.slate_400};
`;

export const FieldValue = styled.span`
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${theme.colors.grey_900};
  word-break: break-word;
  line-height: 1.4;
`;

export const EmDash = styled.span`
  color: ${theme.colors.slate_400};
  font-weight: 400;
`;

export const ContactSubLabel = styled.span`
  font-size: 0.75rem;
  color: ${theme.colors.slate_500};
  font-weight: 400;
`;

export const ContactBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

// ── Legacy row layout (kept for reference) ───────────────────────────

export const FieldRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 0.5rem 0;
  border-bottom: 1px solid ${theme.colors.slate_200};
  gap: 1rem;

  &:last-child {
    border-bottom: none;
  }
`;

// ── Search input container + dropdown ────────────────────────────────

export const SearchContainer = styled.div`
  position: relative;
  width: 320px;
  flex-shrink: 0;
`;

export const DropdownList = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 100;
  margin: 0;
  padding: 0.25rem 0;
  list-style: none;
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.slate_200};
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  max-height: 280px;
  overflow-y: auto;
`;

export const DropdownItem = styled.li<{ $active?: boolean }>`
  padding: 0.5rem 0.875rem;
  font-size: 0.875rem;
  color: ${theme.colors.grey_900};
  cursor: pointer;
  background: ${({ $active }) =>
    $active ? theme.colors.slate_50 : theme.colors.white};

  &:hover {
    background: ${theme.colors.slate_100};
  }

  &:active {
    background: ${theme.colors.slate_200};
  }
`;

export const DropdownEmpty = styled.li`
  padding: 0.625rem 0.875rem;
  font-size: 0.875rem;
  color: ${theme.colors.slate_500};
  pointer-events: none;
`;

// ── Initials avatar circle ────────────────────────────────────────────

export const InitialsAvatar = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${theme.colors.blue_100};
  color: ${theme.colors.blue_700};
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  flex-shrink: 0;
`;

// ── Personnel table section ──────────────────────────────────────────

export const PersonnelSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  margin-top: 1rem;
`;

export const PersonnelSearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

export const UserIdentityCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

export const UserNameBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
`;

export const PillList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  align-items: center;
`;

// ── Pagination controls row ──────────────────────────────────────────

export const PaginationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${theme.colors.slate_200};
  margin-top: 0.5rem;
`;

export const PaginationInfo = styled.span`
  font-size: 0.8rem;
  color: ${theme.colors.slate_500};
`;

// ── Collapsible section ───────────────────────────────────────────────

export const CollapsibleSection = styled.div`
  border: 1px solid ${theme.colors.slate_200};
  border-radius: 8px;
  background: ${theme.colors.white};
  overflow: hidden;
`;

export const CollapsibleHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 1rem 1.25rem;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  gap: 0.75rem;

  &:hover {
    background: ${theme.colors.slate_50};
  }
`;

export const CollapsibleHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

export const CollapsibleBody = styled.div<{ $open: boolean }>`
  display: ${({ $open }) => ($open ? 'block' : 'none')};
  padding: 0 1.25rem 1.25rem;
  border-top: 1px solid ${theme.colors.slate_200};
`;

export const ChevronIcon = styled.span<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  transition: transform 0.2s ease;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  flex-shrink: 0;
`;

// ── Empty / loading states ────────────────────────────────────────────

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 4rem 2rem;
  text-align: center;
  color: ${theme.colors.slate_500};
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 3rem;
`;
